import { useState, useCallback } from 'react';

// Record<string, unknown> exige index signature explícita; interfaces de formulário (sem index
// signature) não a satisfazem estruturalmente, mesmo sendo compatíveis com Record<string, any>.
export const useForm = <T extends Record<string, any>>(
  initialValues: T,
  validate?: (values: T) => Partial<Record<keyof T, string>>
) => {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});

  const handleChange = useCallback(
    (name: keyof T) =>
      (value: T[typeof name]) => {
        setValues(prev => ({ ...prev, [name]: value }));

        // Limpar erro quando começar a digitar
        if (errors[name]) {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
          });
        }
      },
    [errors]
  );

  const handleBlur = useCallback(
    (name: keyof T) => {
      setTouched(prev => ({ ...prev, [name]: true }));

      if (validate) {
        const validationErrors = validate(values);
        if (validationErrors[name]) {
          setErrors(prev => ({ ...prev, [name]: validationErrors[name] as string }));
        }
      }
    },
    [values, validate]
  );

  const validateForm = useCallback(() => {
    if (!validate) return true;

    const validationErrors = validate(values);
    setErrors(validationErrors);
    setTouched(
      Object.keys(values).reduce((acc, key) => {
        acc[key as keyof T] = true;
        return acc;
      }, {} as Partial<Record<keyof T, boolean>>)
    );

    return Object.keys(validationErrors).length === 0;
  }, [values, validate]);

  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  }, [initialValues]);

  return {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    reset,
    setValues,
    setErrors,
  };
};


